import { convertToParens, printNumber, yearRange } from "src/utils";
import Input from "../Inputs/Input";
import { useState } from "react";

export const RATE = function (
  periods: any,
  payment: any,
  present: any,
  future: any,
  type: any,
) {
  let guess = 0.01;
  future = future === undefined ? 0 : future;
  type = type === undefined ? 0 : type;

  // Set maximum epsilon for end of iteration
  var epsMax = 1e-6;

  // Set maximum number of iterations
  var iterMax = 1000000;

  // Implement Newton's method
  var y,
    y0,
    y1,
    x0,
    x1 = 0,
    f = 0,
    i = 0;
  var rate = guess;
  if (Math.abs(rate) < epsMax) {
    y =
      present * (1 + periods * rate) +
      payment * (1 + rate * type) * periods +
      future;
  } else {
    f = Math.exp(periods * Math.log(1 + rate));
    y = present * f + payment * (1 / rate + type) * (f - 1) + future;
  }
  y0 = present + payment * periods + future;
  y1 = present * f + payment * (1 / rate + type) * (f - 1) + future;
  i = x0 = 0;
  x1 = rate;
  while (Math.abs(y0 - y1) > epsMax && i < iterMax) {
    rate = (y1 * x0 - y0 * x1) / (y1 - y0);
    x0 = x1;
    x1 = rate;
    if (Math.abs(rate) < epsMax) {
      y =
        present * (1 + periods * rate) +
        payment * (1 + rate * type) * periods +
        future;
    } else {
      f = Math.exp(periods * Math.log(1 + rate));
      y = present * f + payment * (1 / rate + type) * (f - 1) + future;
    }
    y0 = y1;
    y1 = y;
    ++i;
  }
  console.log("iters", i);
  return Math.round(rate * 1000) / 10;
};

interface EquivalentYieldCalculatorProps {
  calc: any;
  setCalcField?: (field: string) => (value: any) => void;
  showInputs?: boolean;
}

const EquivalentYieldCalculator = ({
  calc,
  setCalcField,
  showInputs = true,
}: EquivalentYieldCalculatorProps) => {
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [selectedCol, setSelectedCol] = useState<number | null>(null);

  const handleSetField = (field: string) => (value: any) => {
    if (setCalcField) {
      setCalcField(field)(value);
    }
  };

  const income = ((calc.withdrawalRate || 0) / 100) * (calc.futureValue || 0);
  const preTaxIncome = Math.round(income / (1 - (calc.taxRate || 0) / 100));

  return (
    <div className="border p-4 rounded-lg bg-white shadow-md print:shadow-none">
      <div className="flex justify-between gap-5">
        {showInputs && (
          <div className="flex flex-col gap-2 w-72">
            <Input
              value={calc.premium}
              subtype="money"
              label="Premium"
              setValue={handleSetField("premium")}
            />
            <Input
              value={calc.yearsOfPremium}
              subtype="number"
              label="Years of Premium"
              setValue={handleSetField("yearsOfPremium")}
            />
            <Input
              value={calc.futureValue}
              subtype="money"
              label="Future Cash Value"
              setValue={handleSetField("futureValue")}
            />
            <Input
              value={calc.withdrawalRate}
              subtype="percent"
              label="LI Withdrawal Rate"
              setValue={handleSetField("withdrawalRate")}
            />
            <Input
              value={income}
              subtype="money"
              label="Income"
              disabled
              setValue={handleSetField("withdrawalRate")}
            />
            <Input
              value={calc.taxRate}
              subtype="percent"
              label="Tax Rate"
              setValue={handleSetField("taxRate")}
            />

            <Input
              value={preTaxIncome}
              subtype="money"
              label="Pre-Tax Income"
              disabled
              setValue={handleSetField("withdrawalRate")}
            />
          </div>
        )}
        <div>
          <table className="w-full border ">
            <thead
              className={`text-sm cursor-pointer text-left sticky z-50 border font-normal!`}
            >
              <tr className="text-lg text-center">
                <th
                  className={`border px-6 py-3 font-medium ${selectedCol === 0 ? "bg-slate-200" : ""}`}
                  onClick={() => setSelectedCol(selectedCol === 0 ? null : 0)}
                >
                  To get a <br /> pre-tax income of:
                </th>
                <th
                  className={`border px-6 py-3 font-medium ${selectedCol === 1 ? "bg-slate-200" : ""}`}
                  onClick={() => setSelectedCol(selectedCol === 1 ? null : 1)}
                >
                  At a <br /> withdrawal rate of:
                </th>
                <th
                  className={`border px-6 py-3 font-medium ${selectedCol === 2 ? "bg-slate-200" : ""}`}
                  onClick={() => setSelectedCol(selectedCol === 2 ? null : 2)}
                >
                  I need a <br /> balance of:
                </th>
                <th
                  className={`border px-6 py-3 font-medium ${selectedCol === 3 ? "bg-slate-200" : ""}`}
                  onClick={() => setSelectedCol(selectedCol === 3 ? null : 3)}
                >
                  Which requires a <br /> rate of return of:
                </th>
              </tr>
            </thead>
            <tbody className="inconsolata border text-center ">
              {yearRange(1, 6).map((w, rowIndex) => {
                const rate = RATE(
                  calc.yearsOfPremium,
                  -calc.premium,
                  0,
                  preTaxIncome / (w / 100),
                  undefined,
                );
                return (
                  <tr
                    key={rowIndex}
                    className={`border ${selectedRow === rowIndex ? "bg-slate-200" : ""}`}
                    onClick={() =>
                      setSelectedRow(selectedRow === rowIndex ? null : rowIndex)
                    }
                  >
                    <td
                      className={`border px-2 py-2 ${selectedCol === 0 ? (selectedRow === rowIndex ? "bg-slate-300" : "bg-slate-200") : selectedRow === rowIndex ? "bg-slate-200" : ""}`}
                    >
                      {printNumber(preTaxIncome)}
                    </td>
                    <td
                      className={`border px-2 py-2 ${selectedCol === 1 ? (selectedRow === rowIndex ? "bg-slate-300" : "bg-slate-200") : selectedRow === rowIndex ? "bg-slate-200" : ""}`}
                    >{`${w}%`}</td>
                    <td
                      className={`border px-2 py-2 ${selectedCol === 2 ? (selectedRow === rowIndex ? "bg-slate-300" : "bg-slate-200") : selectedRow === rowIndex ? "bg-slate-200" : ""}`}
                    >
                      {printNumber(preTaxIncome / (w / 100))}
                    </td>
                    <td
                      className={`border px-2 py-2 ${rate < 0 ? "text-red-600" : ""} ${selectedCol === 3 ? (selectedRow === rowIndex ? "bg-slate-300" : "bg-slate-200") : selectedRow === rowIndex ? "bg-slate-200" : ""}`}
                    >
                      {convertToParens(`${rate.toString()}%`)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EquivalentYieldCalculator;
